/**
 * Markdown → HTML, en el subconjunto que hace falta para comentar un informe.
 *
 * Lo escriben los evaluadores y lo lee el estudiante, así que **primero se escapa todo**
 * y recién después se aplica el formato: ninguna etiqueta que alguien escriba llega
 * al HTML. Por eso es un intérprete propio y chico y no una librería —el proyecto no
 * suma dependencias para esto, igual que `EditorTexto`—, y por eso los enlaces solo
 * aceptan http, https y mailto.
 *
 * Lo que entiende:
 *   párrafos (separados por una línea en blanco) y saltos de línea dentro de ellos,
 *   `# Título` (1 a 3 #), listas con `- ` o `* ` y numeradas con `1. `,
 *   `> cita`, **negrita**, *cursiva* o _cursiva_, `código` y [texto](https://…).
 *
 * Lo que no entiende queda como texto: un comentario escrito sin pensar en Markdown
 * —los que ya existen— se lee igual que antes.
 */

function escapar(texto: string): string {
  return texto.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

function enLinea(texto: string): string {
  let s = escapar(texto);
  // El código primero, y se aparta: lo que va adentro no se formatea.
  const codigos: string[] = [];
  s = s.replace(/`([^`]+)`/g, (_, c) => {
    codigos.push(c);
    return `\u0000${codigos.length - 1}\u0000`;
  });
  s = s.replace(/\[([^\]]+)\]\(((?:https?:\/\/|mailto:)[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer noopener">$1</a>');
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(^|[^*])\*([^*\s][^*]*)\*/g, "$1<em>$2</em>");
  s = s.replace(/(^|[\s(])_([^_\s][^_]*)_(?=[\s.,;:!?)]|$)/g, "$1<em>$2</em>");
  return s.replace(/\u0000(\d+)\u0000/g, (_, i) => `<code>${codigos[Number(i)]}</code>`);
}

const VINETA = /^\s*[-*]\s+/;
const NUMERO = /^\s*\d+[.)]\s+/;

export function markdownAHtml(texto: string): string {
  const lineas = (texto ?? "").replace(/\r\n?/g, "\n").split("\n");
  const html: string[] = [];
  let i = 0;
  while (i < lineas.length) {
    const linea = lineas[i];
    if (!linea.trim()) {
      i++;
      continue;
    }
    const titulo = /^(#{1,3})\s+(.*)$/.exec(linea);
    if (titulo) {
      // Los títulos de un comentario son subtítulos: nunca del tamaño de la página.
      html.push(`<h4>${enLinea(titulo[2])}</h4>`);
      i++;
      continue;
    }
    if (VINETA.test(linea) || NUMERO.test(linea)) {
      const patron = VINETA.test(linea) ? VINETA : NUMERO;
      const tag = patron === VINETA ? "ul" : "ol";
      const items: string[] = [];
      while (i < lineas.length && patron.test(lineas[i])) {
        items.push(`<li>${enLinea(lineas[i].replace(patron, ""))}</li>`);
        i++;
      }
      html.push(`<${tag}>${items.join("")}</${tag}>`);
      continue;
    }
    if (/^\s*>/.test(linea)) {
      const citas: string[] = [];
      while (i < lineas.length && /^\s*>/.test(lineas[i])) {
        citas.push(enLinea(lineas[i].replace(/^\s*>\s?/, "")));
        i++;
      }
      html.push(`<blockquote>${citas.join("<br>")}</blockquote>`);
      continue;
    }
    // Párrafo: hasta la línea en blanco o hasta que empiece otro bloque.
    const parrafo: string[] = [];
    while (
      i < lineas.length &&
      lineas[i].trim() &&
      !/^(#{1,3})\s+/.test(lineas[i]) &&
      !VINETA.test(lineas[i]) &&
      !NUMERO.test(lineas[i]) &&
      !/^\s*>/.test(lineas[i])
    ) {
      parrafo.push(enLinea(lineas[i]));
      i++;
    }
    html.push(`<p>${parrafo.join("<br>")}</p>`);
  }
  return html.join("");
}
